import { Table } from 'antd';
import isempty from 'lodash.isempty';
import cn from 'classnames';
import { isFunction } from 'shared/helpers/utils/is-function';
import type { AppTableProps, AppTableItem } from './types';
import { useTableDataSource } from './hooks/use-table-data-source';
import { useTableChange } from './hooks/use-table-change';
import { useTableColumns } from './hooks/use-table-columns';
import { useTableRowSelection } from './hooks/use-table-row-selection';
import { useTableExpandable } from './hooks/use-table-expandable';
import { getClassNames, getRowClassNames } from './Table.utils';
import { useEditableCells } from './hooks/use-editable-table';

export const AppTable = <TRecord extends AppTableItem>(props: AppTableProps<TRecord>) => {
  const {
    scroll = { x: '100%', scrollToFirstRowOnChange: false },
    className,
    rowClassName,
    rowKey,
    size = 'large',
    dataSource: dataSourceProp,
    columns: columnsProp,
    onChange: onChangeProp,
    sorters = [],
    multipleSorter = false,
    showSorterTooltip = false,
    filters = [],
    pagination = {},
    paginationVisible = true,
    bordered = true,
    groupingConfig,
    rowSelection: rowSelectionProp,
    expandable: expandableProp,
    editable: editableProp,
    components: componentsProp,
    handleSave,
    ...rest
  } = props;
  const { onTableChange, onFilterChange } = useTableChange<TRecord>({
    onChange: onChangeProp,
    sorters,
    filters,
    pagination,
    paginationVisible,
  });

  const dataSource = useTableDataSource<TRecord>({
    dataSource: dataSourceProp,
    rowKey,
    groupingConfig,
  });

  const columns = useTableColumns<TRecord>({
    columns: columnsProp,
    sorters,
    multipleSorter,
    filters,
    onFilterChange,
    groupingConfig,
  });

  const rowSelection = useTableRowSelection<TRecord>({
    rowSelection: rowSelectionProp,
    initialDataSource: dataSourceProp,
    groupingConfig,
  });

  const expandable = useTableExpandable<TRecord>({
    dataSource,
    expandable: expandableProp,
  });

  const editable = useEditableCells<TRecord>({
    columns,
    editable: editableProp,
    components: componentsProp,
    handleSave,
  });

  const getTableRowClassNames = (record: TRecord, index: number, indent: number) => {
    const tableRowClassNames = getRowClassNames(index, rest.onRow);
    const passedRowClassNames = isFunction(rowClassName)
      ? rowClassName(record, index, indent)
      : rowClassName;

    return cn(tableRowClassNames, passedRowClassNames);
  };

  const classNames = getClassNames(!dataSource.length, className);
  const tablePagination = paginationVisible && !isempty(pagination) ? pagination : false;

  return (
    <Table
      className={classNames}
      rowClassName={getTableRowClassNames}
      dataSource={dataSource}
      columns={editable.columns as any}
      onChange={onTableChange}
      pagination={tablePagination}
      rowSelection={rowSelection}
      showSorterTooltip={showSorterTooltip}
      scroll={scroll}
      expandable={expandable}
      bordered={bordered}
      size={size}
      components={editable.components}
      {...rest}
    />
  );
};
